import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import BottomBanner from '../components/BottomBanner';
import { getLocalizedValue, getCurrentLanguage, LANGUAGE_CHANGE_EVENT } from '../config/languageConfig';
import { t } from '../config/translations';
import { searchRestaurantsByFranchise } from '../api/menuService';
import { useAddress } from '../contexts/AddressContext';
import { GuideContent, guideContents } from '../data/guides';

const GuideDetailPage: React.FC = () => {
  const { guideId } = useParams<{ guideId: string }>();
  const navigate = useNavigate();
  const { address } = useAddress();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [showAddressAffordance, setShowAddressAffordance] = useState(false);
  const [arrowPosition, setArrowPosition] = useState(60); // 기본값
  const addressButtonRef = useRef<HTMLDivElement>(null);

  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, []);

  // Address 버튼 위치 계산 (NavigationBar와 동일한 로직)
  useEffect(() => {
    const calculateArrowPosition = () => {
      if (showAddressAffordance) {
        // NavigationBar의 Address 버튼 위치를 계산
        const navBar = document.querySelector('.fixed.bottom-0');
        if (navBar) {
          const addressButton = navBar.querySelector('div:nth-child(4)'); // Address는 4번째 버튼
          if (addressButton) {
            const buttonRect = addressButton.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            const buttonCenter = buttonRect.left + buttonRect.width / 2;
            const screenCenter = windowWidth / 2;
            const offset = buttonCenter - screenCenter;
            setArrowPosition(offset);
          }
        }
      }
    };

    calculateArrowPosition();
    window.addEventListener('resize', calculateArrowPosition);
    
    return () => {
      window.removeEventListener('resize', calculateArrowPosition);
    };
  }, [showAddressAffordance]);

  // 스크롤 시 어포던스 해제 (NavigationBar와 동일한 로직)
  useEffect(() => {
    if (!showAddressAffordance) return;
    
    const handleScroll = () => {
      setShowAddressAffordance(false);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showAddressAffordance]);

  // 주소가 설정되지 않았을 때 어포던스 표시
  const handleRequireAddress = () => {
    setShowAddressAffordance(true);
  };

  // 프랜차이즈 ID로 가게 검색 후 상세 페이지로 이동
  const handleFranchiseSearch = async (franchiseId: string, franchiseName?: string) => {
    if (!address?.lat || !address?.lng) {
      handleRequireAddress();
      return;
    }

    try {
      const restaurants = await searchRestaurantsByFranchise(
        address.lat,
        address.lng,
        franchiseId,
        1,
        address.addressId // addressId 추가
      );

      if (restaurants.length > 0) {
        const restaurant = restaurants[0];
        navigate(`/restaurant/${restaurant.id}`);
      } else {
        // 검색 결과가 없을 때 프랜차이즈 이름으로 검색 결과 페이지로 이동
        const brandName = franchiseName || franchiseId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
        const params = new URLSearchParams({ 
          menuName: brandName,
          searchType: 'brand' 
        });
        navigate(`/search-results?${params.toString()}`);
      }
    } catch (error) {
      console.error('Error searching franchise restaurants:', error);
      // 에러 발생 시에도 검색 결과 페이지로 이동
      const brandName = franchiseName || franchiseId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
      const params = new URLSearchParams({ 
        menuName: brandName,
        searchType: 'brand' 
      });
      navigate(`/search-results?${params.toString()}`);
    }
  };

  // 메뉴 검색 - 주소 체크 추가
  const handleMenuSearch = (menuName: string, categoryId?: string) => {
    if (!address?.lat || !address?.lng) {
      handleRequireAddress();
      return;
    }

    const params = new URLSearchParams({ menuName });
    if (categoryId) {
      params.append('categoryId', categoryId);
    }
    navigate(`/search-results?${params.toString()}`);
  };

  // 가이드 ID에 따른 컨텐츠 데이터
  const getGuideContent = (id: string): GuideContent | null => {
    // 데이터 파일에서 가져오기
    if (guideContents[id]) {
      const content = guideContents[id];
      // onClick 함수를 추가하여 반환
      return {
        ...content,
        inlineButtons: content.inlineButtons?.map(button => ({
          ...button,
          onClick: () => {
            if (button.url) {
              // URL로 이동 (현재 창에서)
              window.location.href = button.url;
            } else if (button.franchiseId) {
              // 프랜차이즈 검색
              handleFranchiseSearch(button.franchiseId, button.franchiseName);
            } else if (button.menuName) {
              // 메뉴 검색
              handleMenuSearch(button.menuName, button.categoryId);
            }
          }
        }))
      };
    }

    switch (id) {
      default:
        return null;
    }
  };

  const content = getGuideContent(guideId || '1');

  if (!content) {
    return (
      <>
        <BackHeader />
        <div className="p-4 pb-20">
          <div className="text-center text-gray-500">
            Guide not found
          </div>
        </div>
        <NavigationBar />
      </>
    );
  }

  // 마크다운 텍스트에서 YouTube 링크를 임베드 컴포넌트로 변환
  const processYouTubeLinks = (markdownText: string): string => {
    // YouTube URL 패턴을 찾아서 특별한 마커로 교체
    const youtubeUrlPattern = /https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/g;
    
    return markdownText.replace(youtubeUrlPattern, (match, _, __, videoId) => {
      // 특별한 마커로 교체 (독립적인 블록으로 처리)
      return `\n\nYOUTUBE_EMBED:${videoId}\n\n`;
    });
  };

  // 유튜브 링크를 임베드로 변환하는 컴포넌트
  const YouTubeEmbed: React.FC<{ videoId: string }> = ({ videoId }) => {
    return (
      <div className="my-4">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <BackHeader />
      <div key={currentLang} className="p-4 pb-20">
        {/* 헤더 섹션 */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {getLocalizedValue(content.title, {
              en: content.titleEn,
              ja: content.titleJa,
              'zh-CN': content.titleZhCn,
              'zh-TW': content.titleZhTw
            })}
          </h1>
          <p className="text-gray-600 text-sm">
            {getLocalizedValue(content.subtitle, {
              en: content.subtitleEn,
              ja: content.subtitleJa,
              'zh-CN': content.subtitleZhCn,
              'zh-TW': content.subtitleZhTw
            })}
          </p>
        </div>

        {/* 마크다운 컨텐츠 */}
        <div className="prose prose-sm max-w-none mb-6">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              // 이미지 스타일링
              img: ({node, ...props}) => {
                // src가 비어있으면 렌더링하지 않음
                if (!props.src || props.src.trim() === '') {
                  return null;
                }
                
                return (
                  <img 
                    {...props} 
                    className="w-full h-48 object-cover rounded-lg mb-4"
                    loading="lazy"
                    alt={props.alt || "Guide image"}
                  />
                );
              },
              // h1 스타일링 - 인라인 버튼 처리
              h1: ({node, children, ...props}) => {
                const textContent = children?.toString() || '';
                // 버튼 식별자 패턴 매칭: (버튼 1), (버튼 2), etc.
                const buttonMatch = textContent.match(/^\(버튼 (\d+)\)$/);
                if (buttonMatch) {
                  const buttonId = buttonMatch[1]; // 숫자 부분 추출
                  const inlineButton = content.inlineButtons?.find(btn => btn.id === buttonId);
                  if (inlineButton) {
                    return (
                      <div className="my-4">
                        <button
                          onClick={inlineButton.onClick}
                          className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                        >
                          {getLocalizedValue(inlineButton.text, {
                            en: inlineButton.textEn,
                            ja: inlineButton.textJa,
                            'zh-CN': inlineButton.textZhCn,
                            'zh-TW': inlineButton.textZhTw
                          })}
                        </button>
                      </div>
                    );
                  }
                }
                return (
                  <h1 {...props} className="text-2xl font-bold text-gray-900 mt-6 mb-3">
                    {children || "Heading"}
                  </h1>
                );
              },
              // 제목 스타일링
              h2: ({node, children, ...props}) => (
                <h2 {...props} className="text-xl font-bold text-gray-900 mt-6 mb-3 text-left">
                  {children || "Heading"}
                </h2>
              ),
              // 구분선 스타일링
              hr: ({node, ...props}) => (
                <hr {...props} className="my-6 border-gray-200" />
              ),
              // 문단 스타일링 및 YouTube 임베드 처리
              p: ({node, children, ...props}) => {
                // 문단 내용이 YouTube 임베드 마커인지 확인
                const textContent = children?.toString() || '';
                const youtubeMatch = textContent.match(/^YOUTUBE_EMBED:([a-zA-Z0-9_-]{11})$/);
                
                if (youtubeMatch) {
                  const videoId = youtubeMatch[1];
                  return <YouTubeEmbed videoId={videoId} />;
                }
                
                return <p {...props} className="text-gray-600 text-sm leading-relaxed mb-4 text-left">{children}</p>;
              },
              // 강조 텍스트 스타일링 (YouTube 처리 제거)
              strong: ({node, children, ...props}) => {
                return <strong {...props}>{children}</strong>;
              },
              // 링크 스타일링 (YouTube 링크 처리 제거)
              a: ({node, children, ...props}) => (
                <a {...props} className="text-blue-600 hover:text-blue-800">
                  {children || props.href || "Link"}
                </a>
              )
            }}
          >
            {processYouTubeLinks(getLocalizedValue(content.markdownContent, {
              en: content.markdownContentEn,
              ja: content.markdownContentJa,
              'zh-CN': content.markdownContentZhCn,
              'zh-TW': content.markdownContentZhTw
            }))}
          </ReactMarkdown>
        </div>

        {/* 기존 버튼들 (필요시) */}
        {content.buttons && content.buttons.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Find Restaurants
            </h3>
            {content.buttons.map((button) => (
              <button
                key={button.id}
                onClick={button.onClick}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                {button.text}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Address 어포던스 툴팁 */}
      {showAddressAffordance && (
        <div 
          className="address-tooltip"
          style={{
            '--arrow-offset': `${arrowPosition}px`
          } as React.CSSProperties}
          ref={addressButtonRef}
          dangerouslySetInnerHTML={{ __html: t('nav.address.affordance') }}
        />
      )}
      
      <BottomBanner />
      <NavigationBar />
    </>
  );
};

export default GuideDetailPage; 